import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowDownToLine, ArrowLeft, ArrowUpFromLine, Battery, FileText, History, Loader2,
  MessageSquarePlus, Pencil, RefreshCw, Repeat, Cpu, Truck, Link2,
} from 'lucide-react';
import { api } from '@/lib/api';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EstadoBadge from '@/components/dispositivos/EstadoBadge';
import FormularioDispositivo from '@/components/dispositivos/FormularioDispositivo';
import ModalInstalar from '@/components/dispositivos/ModalInstalar';
import ModalRetirar from '@/components/dispositivos/ModalRetirar';
import ModalEstado from '@/components/dispositivos/ModalEstado';
import ModalPilas from '@/components/dispositivos/ModalPilas';
import ModalIncidencia from '@/components/dispositivos/ModalIncidencia';
import LineaTiempo from '@/components/dispositivos/LineaTiempo';
import DocumentosDispositivo from '@/components/dispositivos/DocumentosDispositivo';
import { ChipBateria, ChipConexion } from '@/components/dispositivos/Chips';
import { useCatalogosDispositivos } from '@/components/dispositivos/catalogos';
import {
  customFieldDe,
  ESTADOS_CON_UBICACION,
  estadoGarantia,
  fechaCorta,
  fechaHora,
  MARCA_LABEL,
  MOTIVO_RETIRADA_LABEL,
  textoDonde,
  TIPO_LABEL,
  type AccionDispositivo,
  type Dispositivo,
  type FichaDispositivo,
  type MotivoRetirada,
} from '@/lib/dispositivos';

type Pestana = 'datos' | 'historial' | 'incidencias' | 'documentos';

const PESTANAS: { clave: Pestana; etiqueta: string; icono: React.ElementType }[] = [
  { clave: 'datos', etiqueta: 'Datos', icono: Cpu },
  { clave: 'historial', etiqueta: 'Historial', icono: History },
  { clave: 'incidencias', etiqueta: 'Incidencias', icono: AlertTriangle },
  { clave: 'documentos', etiqueta: 'Documentos', icono: FileText },
];

/**
 * La ficha de un dispositivo: el ID en grande —es lo único que no cambia
 * cuando el aparato cambia de casa—, el alias debajo, el estado, dónde está y
 * las acciones que caben en ese estado. El resto son cuatro pestañas.
 */
export default function DispositivoDetallePage() {
  const { id = '' } = useParams();
  const { catalogos } = useCatalogosDispositivos();
  const [params, setParams] = useSearchParams();

  const pestana = (params.get('tab') as Pestana) || 'datos';
  const cambiarPestana = (p: Pestana) => {
    const siguiente = new URLSearchParams(params);
    siguiente.set('tab', p);
    setParams(siguiente, { replace: true });
  };

  const [ficha, setFicha] = useState<FichaDispositivo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<string | null>(null);
  const [modal, setModal] = useState<null | 'editar' | 'instalar' | 'mover' | 'retirar' | 'estado' | 'pilas' | 'incidencia' | 'nota'>(null);
  const [consultando, setConsultando] = useState(false);
  const [reintentando, setReintentando] = useState(false);
  const [nota, setNota] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setFicha(await api.getDispositivo(id));
      setFallo(null);
    } catch (err) {
      setFallo(err instanceof Error ? err.message : 'No se ha podido cargar la ficha');
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => { void cargar(); }, [cargar]);

  /** Tras cualquier acción: lo que devuelve el servidor sustituye a lo que había, y se relee lo derivado. */
  const trasAccion = (r: AccionDispositivo) => {
    setFicha((f) => (f ? { ...f, dispositivo: r.dispositivo, historial: r.historial, casas: r.casas ?? f.casas, transiciones: r.transiciones ?? f.transiciones } : f));
    void cargar();
  };

  const consultar = async () => {
    setConsultando(true);
    setAviso(null);
    try {
      const r = await api.consultarDispositivo(id);
      trasAccion(r);
      setAviso(r.cambios && r.cambios.length > 0 ? 'Leído; hay cambios respecto a la última instantánea (ver Historial).' : 'Leído; sin cambios.');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se ha podido consultar');
    } finally {
      setConsultando(false);
    }
  };

  const reintentarHostify = async () => {
    setReintentando(true);
    try {
      const r = await api.reintentarHostifyDispositivo(id);
      trasAccion(r);
      if (r.fallos && r.fallos.length > 0) alert(r.fallos.join('\n'));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se ha podido reintentar');
    } finally {
      setReintentando(false);
    }
  };

  const anadirNota = async () => {
    if (!nota.trim()) return;
    try {
      trasAccion(await api.anadirNotaDispositivo(id, nota.trim()));
      setNota('');
      setModal(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'No se ha podido guardar la nota');
    }
  };

  if (cargando && !ficha) return <LoadingSpinner />;
  if (fallo || !ficha) {
    return (
      <div className="card">
        <p className="text-sm text-red-600">{fallo || 'Dispositivo no encontrado'}</p>
        <p className="text-xs text-sereno-dark/50 mt-1">Si viene de Aperturas › Salud, puede que este aparato aún no esté en el inventario: sincroniza desde Dispositivos.</p>
        <Link to="/dispositivos" className="btn-secondary mt-4 inline-block">Volver al inventario</Link>
      </div>
    );
  }

  const { dispositivo: d, historial, casas, enlazados, enlazadoA, transiciones } = ficha;
  const instalado = d.estado === 'instalado';
  const enStock = d.estado === 'stock';
  const pendientesHostify = d.instalaciones.filter((i) => i.hostifyPendiente);
  const puedeConsultar = d.tieneApi || ((d.tipo === 'keypad' || d.tipo === 'sensor_puerta') && !!d.enlazadoA);
  const puedeCambiarEstado = transiciones.length > 0 || ESTADOS_CON_UBICACION.includes(d.estado);
  const garantia = estadoGarantia(d.compra);
  const modeloLabel = d.modelo ? catalogos.modeloLabel[d.modelo] || d.modelo : null;

  return (
    <div>
      <Link to="/dispositivos" className="inline-flex items-center gap-1.5 text-sm text-sereno-dark/50 hover:text-sereno-dark mb-4">
        <ArrowLeft size={16} /> Inventario
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold font-mono truncate">{d.idFabricante}</h1>
            <EstadoBadge estado={d.estado} />
          </div>
          <p className="text-sm text-sereno-dark/60">
            {d.alias || <span className="text-sereno-dark/30">sin alias</span>}
          </p>
          <p className="text-sm text-sereno-dark/50 mt-0.5">
            {MARCA_LABEL[d.marca]} · {TIPO_LABEL[d.tipo]}{modeloLabel ? ` · ${modeloLabel}` : ''} · {textoDonde(d)}
            {instalado && d.instalaciones.find((i) => i.principal) ? ` · desde ${fechaCorta(d.instalaciones.find((i) => i.principal)!.desde)}` : ''}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {(enStock || instalado) && (
            <button onClick={() => setModal('instalar')} className="btn-secondary flex items-center gap-1.5 text-sm">
              <ArrowDownToLine size={16} /> <span className="hidden sm:inline">{instalado ? 'Otra casa más' : 'Instalar'}</span>
            </button>
          )}
          {instalado && (
            <>
              <button onClick={() => setModal('mover')} className="btn-secondary flex items-center gap-1.5 text-sm">
                <Truck size={16} /> <span className="hidden sm:inline">Mover</span>
              </button>
              <button onClick={() => setModal('retirar')} className="btn-secondary flex items-center gap-1.5 text-sm">
                <ArrowUpFromLine size={16} /> <span className="hidden sm:inline">Retirar</span>
              </button>
            </>
          )}
          {puedeCambiarEstado && !instalado && (
            <button onClick={() => setModal('estado')} className="btn-secondary flex items-center gap-1.5 text-sm">
              <Repeat size={16} /> <span className="hidden sm:inline">Cambiar estado</span>
            </button>
          )}
          <button onClick={() => setModal('pilas')} className="btn-secondary flex items-center gap-1.5 text-sm">
            <Battery size={16} /> <span className="hidden sm:inline">Pilas</span>
          </button>
          <button onClick={() => setModal('incidencia')} className="btn-secondary flex items-center gap-1.5 text-sm">
            <AlertTriangle size={16} /> <span className="hidden sm:inline">Incidencia</span>
          </button>
          {puedeConsultar && (
            <button onClick={() => void consultar()} disabled={consultando} className="btn-secondary flex items-center gap-1.5 text-sm disabled:opacity-50" title="Una lectura en vivo de la API del fabricante">
              <RefreshCw size={16} className={consultando ? 'animate-spin' : ''} /> <span className="hidden sm:inline">Consultar ahora</span>
            </button>
          )}
          <button onClick={() => setModal('editar')} className="btn-primary flex items-center gap-1.5 text-sm">
            <Pencil size={16} /> <span className="hidden sm:inline">Editar</span>
          </button>
        </div>
      </div>

      {aviso && <Aviso tono="blue">{aviso}</Aviso>}
      {pendientesHostify.length > 0 && (
        <Aviso tono="amber">
          <span className="flex-1">
            Instalado en {pendientesHostify.map((i) => i.nickname).join(', ')} sin escribir el custom field en Hostify: las llaves de las reservas no lo ven.
          </span>
          <button onClick={() => void reintentarHostify()} disabled={reintentando} className="btn-secondary text-xs ml-2 disabled:opacity-50">
            {reintentando ? 'Reintentando…' : 'Reintentar'}
          </button>
        </Aviso>
      )}
      {d.estado === 'cuarentena' && (
        <Aviso tono="amber">En cuarentena: se retiró por avería. No se puede instalar hasta que alguien lo marque como revisado en «Cambiar estado».</Aviso>
      )}
      {!d.modelo && (
        <Aviso tono="gray">Sin modelo. En las smartlocks decide si hace falta bridge: complétalo en «Editar»{d.snapshot?.hardware ? ` (hardware ${d.snapshot.hardware} según la API)` : ''}.</Aviso>
      )}

      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-1 -mb-px overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          {PESTANAS.map(({ clave, etiqueta, icono: Icono }) => (
            <button
              key={clave}
              onClick={() => cambiarPestana(clave)}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 sm:py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${
                pestana === clave ? 'border-sereno-dark text-sereno-dark font-medium' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icono size={15} />
              {etiqueta}
              {clave === 'incidencias' && d.resumen.incidencias > 0 && <span className="text-xs text-sereno-dark/40">{d.resumen.incidencias}</span>}
              {clave === 'documentos' && d.documentos.length > 0 && <span className="text-xs text-sereno-dark/40">{d.documentos.length}</span>}
            </button>
          ))}
        </nav>
      </div>

      {pestana === 'datos' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="card">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Identidad</h3>
              <Datos>
                <Dato etiqueta="ID"><span className="font-mono">{d.idFabricante}</span></Dato>
                <Dato etiqueta="Número de serie"><span className="font-mono">{d.numeroSerie || '—'}</span></Dato>
                <Dato etiqueta="Marca y tipo">{MARCA_LABEL[d.marca]} · {TIPO_LABEL[d.tipo]}</Dato>
                <Dato etiqueta="Modelo">{modeloLabel || <span className="text-amber-700">por confirmar</span>}</Dato>
                <Dato etiqueta="Alias">{d.alias || '—'}</Dato>
                <Dato etiqueta="Id interno"><span className="font-mono text-xs text-sereno-dark/50">{d.id}</span></Dato>
              </Datos>
            </div>

            <div className="card">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Dónde está</h3>
              {instalado ? (
                <ul className="space-y-1.5">
                  {d.instalaciones.map((i) => (
                    <li key={i.listingId} className="text-sm flex items-center gap-2 flex-wrap">
                      <Link to={`/dispositivos?listingId=${i.listingId}`} className="font-medium hover:underline">{i.nickname}</Link>
                      {i.principal && d.instalaciones.length > 1 && <span className="badge bg-gray-100 text-gray-600 text-[10px]">principal</span>}
                      {i.rol === 'smartlock2' && <span className="badge bg-gray-100 text-gray-600 text-[10px]">segunda smartlock (1840)</span>}
                      <span className="text-sereno-dark/40 text-xs">desde {fechaCorta(i.desde)}</span>
                      {customFieldDe(d.tipo, i.rol) && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${i.hostifyPendiente ? 'bg-amber-100 text-amber-700' : 'bg-green-50 text-green-700'}`}>
                          Hostify {customFieldDe(d.tipo, i.rol)} {i.hostifyPendiente ? 'pendiente' : 'ok'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm">{textoDonde(d)}{d.estado !== 'baja' && d.estado !== 'perdido' ? ` · desde ${fechaCorta(d.estadoDesde)}` : ''}</p>
              )}
            </div>

            <div className="card">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Compra y garantía</h3>
              <Datos>
                <Dato etiqueta="Fecha de compra">{fechaCorta(d.compra.fecha)}</Dato>
                <Dato etiqueta="Proveedor">{d.compra.proveedor || '—'}</Dato>
                <Dato etiqueta="Precio">{d.compra.precio != null ? `${d.compra.precio.toLocaleString('es-ES')} €` : '—'}</Dato>
                <Dato etiqueta="Factura">{d.compra.factura || '—'}</Dato>
                <Dato etiqueta="Garantía">
                  <span className={garantia.vigente === null ? 'text-sereno-dark/40' : garantia.vigente ? 'text-green-700' : ''}>{garantia.texto}</span>
                </Dato>
              </Datos>
            </div>

            <div className="card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Notas</h3>
                <button onClick={() => setModal('nota')} className="btn-ghost text-xs flex items-center gap-1"><MessageSquarePlus size={14} /> Añadir nota al historial</button>
              </div>
              {d.notas ? <p className="text-sm whitespace-pre-wrap">{d.notas}</p> : <p className="text-sm text-sereno-dark/40">Sin notas fijas. Las notas con fecha van al historial.</p>}
            </div>
          </div>

          <div className="space-y-6">
            <div className="card">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Estado en vivo</h3>
              {d.snapshot ? (
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-3"><ChipConexion snapshot={d.snapshot} /><ChipBateria snapshot={d.snapshot} /></div>
                  {d.snapshot.firmware && <p><span className="text-sereno-dark/50">Firmware:</span> {d.snapshot.firmware}</p>}
                  {d.snapshot.hardware && <p><span className="text-sereno-dark/50">Hardware:</span> {d.snapshot.hardware}</p>}
                  {d.resumen.ultimoEventoNuki && <p><span className="text-sereno-dark/50">Último evento Nuki:</span> {fechaHora(d.resumen.ultimoEventoNuki)}</p>}
                  <p className="text-xs text-sereno-dark/40">Leído el {fechaHora(d.snapshot.en)}</p>
                  {Object.keys(d.snapshot.extra).length > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-sereno-dark/50">Todo lo que devuelve la API</summary>
                      <pre className="mt-1 bg-gray-50 rounded p-2 overflow-x-auto">{JSON.stringify(d.snapshot.extra, null, 2)}</pre>
                    </details>
                  )}
                </div>
              ) : (
                <p className="text-sm text-sereno-dark/40">{d.tieneApi ? 'Todavía no se ha leído. Pulsa «Consultar ahora».' : 'Este tipo no tiene API.'}</p>
              )}
            </div>

            <div className="card">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Enlaces</h3>
              <div className="space-y-2 text-sm">
                {enlazadoA ? (
                  <p className="flex items-center gap-1.5">
                    <Link2 size={14} className="text-sereno-dark/40" />
                    <span className="text-sereno-dark/50">{d.tipo === 'bot' ? 'Su hub:' : 'Su smartlock:'}</span>
                    <Link to={`/dispositivos/${enlazadoA.id}`} className="font-mono hover:underline">{enlazadoA.idFabricante}</Link>
                    {enlazadoA.alias && <span className="text-sereno-dark/40 text-xs truncate">{enlazadoA.alias}</span>}
                  </p>
                ) : (
                  (d.tipo === 'keypad' || d.tipo === 'sensor_puerta' || d.tipo === 'bot') && (
                    <p className="text-amber-700 text-xs">Sin enlazar a {d.tipo === 'bot' ? 'su hub' : 'su smartlock'}. Ponlo en «Editar».</p>
                  )
                )}
                {d.tipo === 'bot' && d.hubDeviceId && (
                  <p className="text-xs text-sereno-dark/50">
                    Según SwitchBot cuelga del hub <span className="font-mono">{d.hubDeviceId}</span>
                    {enlazadoA && enlazadoA.idFabricante !== d.hubDeviceId && <span className="text-red-600"> — no coincide con el enlace</span>}
                  </p>
                )}
                {enlazados.length > 0 && (
                  <div>
                    <p className="text-sereno-dark/50 text-xs mb-1">Cuelgan de este:</p>
                    <ul className="space-y-1">
                      {enlazados.map((e) => (
                        <li key={e.id} className="flex items-center gap-2">
                          <Link to={`/dispositivos/${e.id}`} className="hover:underline">{TIPO_LABEL[e.tipo]} <span className="font-mono text-xs">{e.idFabricante}</span></Link>
                          <EstadoBadge estado={e.estado} pequeno />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {!enlazadoA && enlazados.length === 0 && !['keypad', 'sensor_puerta', 'bot'].includes(d.tipo) && (
                  <p className="text-sereno-dark/40">Nada enlazado.</p>
                )}
              </div>
            </div>

            <div className="card">
              <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Resumen</h3>
              <Datos>
                <Dato etiqueta="Incidencias">{d.resumen.incidencias}{d.resumen.incidencias90d > 0 ? ` (${d.resumen.incidencias90d} en 90 días)` : ''}</Dato>
                <Dato etiqueta="Última incidencia">{d.resumen.ultimaIncidencia ? `${catalogos.incidenciaLabel[d.resumen.ultimaIncidencia.tipo] || d.resumen.ultimaIncidencia.tipo}, ${fechaCorta(d.resumen.ultimaIncidencia.fecha)}` : '—'}</Dato>
                <Dato etiqueta="Últimas pilas">{fechaCorta(d.resumen.ultimoCambioPilas)}</Dato>
                <Dato etiqueta="Casas por las que ha pasado">{casas.length}</Dato>
                <Dato etiqueta="Sincronizado">{d.sincronizadoEn ? fechaHora(d.sincronizadoEn) : 'nunca'}</Dato>
              </Datos>
            </div>
          </div>
        </div>
      )}

      {pestana === 'historial' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card">
            <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-2">Todo lo apuntado</h3>
            <LineaTiempo entradas={historial} onBorrar={async (e) => trasAccion(await api.borrarEntradaDispositivo(d.id, e.id))} />
          </div>
          <div className="card">
            <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Dónde ha estado</h3>
            {casas.length === 0 ? (
              <p className="text-sm text-sereno-dark/40">En ninguna casa todavía.</p>
            ) : (
              <ul className="space-y-3">
                {casas.map((c) => (
                  <li key={c.listingId}>
                    <p className="text-sm font-medium">{c.nickname}</p>
                    <ul className="text-xs text-sereno-dark/50 space-y-0.5">
                      {c.tramos.map((t, i) => (
                        <li key={i}>
                          {fechaCorta(t.desde)} → {t.hasta ? fechaCorta(t.hasta) : 'hoy'}
                          {t.motivo ? ` · ${MOTIVO_RETIRADA_LABEL[t.motivo as MotivoRetirada] ?? t.motivo}` : ''}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {pestana === 'incidencias' && (
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Registro de incidencias</h3>
            <button onClick={() => setModal('incidencia')} className="btn-secondary text-xs">Apuntar</button>
          </div>
          <LineaTiempo entradas={historial} filtro={['incidencia']} onBorrar={async (e) => trasAccion(await api.borrarEntradaDispositivo(d.id, e.id))} />
        </div>
      )}

      {pestana === 'documentos' && (
        <DocumentosDispositivo dispositivo={d} onActualizado={(nuevo: Dispositivo) => { setFicha((f) => (f ? { ...f, dispositivo: nuevo } : f)); void cargar(); }} />
      )}

      <Modal isOpen={modal === 'editar'} onClose={() => setModal(null)} title="Editar dispositivo" size="xl">
        <FormularioDispositivo
          dispositivo={d}
          onGuardar={async (datos) => {
            const { dispositivo } = await api.actualizarDispositivo(d.id, datos);
            setFicha((f) => (f ? { ...f, dispositivo } : f));
            setModal(null);
            void cargar();
          }}
          onCancelar={() => setModal(null)}
        />
      </Modal>

      {(modal === 'instalar' || modal === 'mover') && (
        <ModalInstalar
          abierto
          dispositivo={d}
          modo={modal}
          onCerrar={() => setModal(null)}
          onGuardar={async (datos) => trasAccion(modal === 'mover' ? await api.moverDispositivo(d.id, datos) : await api.instalarDispositivo(d.id, datos))}
        />
      )}
      {modal === 'retirar' && (
        <ModalRetirar abierto dispositivo={d} onCerrar={() => setModal(null)} onGuardar={async (datos) => trasAccion(await api.retirarDispositivo(d.id, datos))} />
      )}
      {modal === 'estado' && (
        <ModalEstado abierto dispositivo={d} transiciones={transiciones} onCerrar={() => setModal(null)} onGuardar={async (datos) => trasAccion(await api.cambiarEstadoDispositivo(d.id, datos))} />
      )}
      {modal === 'pilas' && (
        <ModalPilas abierto onCerrar={() => setModal(null)} onGuardar={async (datos) => trasAccion(await api.registrarPilasDispositivo(d.id, datos))} />
      )}
      {modal === 'incidencia' && (
        <ModalIncidencia abierto onCerrar={() => setModal(null)} onGuardar={async (datos) => trasAccion(await api.registrarIncidenciaDispositivo(d.id, datos))} />
      )}

      <Modal isOpen={modal === 'nota'} onClose={() => setModal(null)} title="Nota al historial" size="sm">
        <textarea className="input" rows={4} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Queda con fecha y con quién la escribió." />
        <div className="flex justify-end gap-2 pt-4">
          <button type="button" onClick={() => setModal(null)} className="btn-secondary">Cancelar</button>
          <button type="button" onClick={() => void anadirNota()} disabled={!nota.trim()} className="btn-primary disabled:opacity-50">Guardar</button>
        </div>
      </Modal>
      {cargando && <Loader2 size={14} className="animate-spin text-sereno-dark/30 fixed bottom-4 right-4" />}
    </div>
  );
}

function Aviso({ tono, children }: { tono: 'amber' | 'red' | 'blue' | 'gray'; children: React.ReactNode }) {
  const estilos = {
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    blue: 'bg-sereno-blue/30 border-sereno-blue text-sereno-dark',
    gray: 'bg-gray-50 border-gray-200 text-sereno-dark/70',
  };
  return <div className={`rounded-lg border p-3 mb-4 text-sm flex items-center gap-2 ${estilos[tono]}`}>{children}</div>;
}

function Datos({ children }: { children: React.ReactNode }) {
  return <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">{children}</dl>;
}

function Dato({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-sereno-dark/50">{etiqueta}</dt>
      <dd>{children}</dd>
    </div>
  );
}
